import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  StyledTableRow,
  HeaderCell,
  StyledCheckbox,
  StyledTableCell,
  SelectAllText,
} from '../UserPreferences.styled';
import { useDesignatedOperatorRelations } from '@/hooks';
import axios from '../../../common/api/request';
import { UserPreference } from '@/types/userPreferences.ts';

// ── Local types ──────────────────────────────────────────────────────────────

interface ModuleColumn {
  id: string;
  name: string;
}

/** One row in the editable matrix (local working copy). */
export interface UserPrefRow {
  /** DO relation id from Settings (used for display, not for user-pref save). */
  settingsId: number | null;
  originalDO: string;
  targetDO: string;
  year: number;
  /** module-name → enabled (user's current selection). */
  modules: Record<string, boolean>;
  /** module-name → editable flag from Settings. */
  moduleEditability: Record<string, boolean>;
  /** module-name → enabled flag from Settings (if false in Settings, checkbox is disabled). */
  moduleSettingsEnabled: Record<string, boolean>;
}


// ── Props ────────────────────────────────────────────────────────────────────

/** Imperative handle exposed via ref. */
export interface UserModuleMatrixHandle {
  /** Returns the current (all) rows — including hidden ones. Used by the parent to build the save payload. */
  getRows: () => UserPrefRow[];
  /** Returns only rows whose modules differ from the initial (loaded) state. */
  getChangedRows: () => UserPrefRow[];
  /** Resets all user selections to unchecked (clears preferences for the current year). */
  resetRows: () => void;
  /** Checks all enabled (non-disabled) checkboxes for the current year. */
  checkAll: () => void;
  /** Unchecks all enabled (non-disabled) checkboxes for the current year. */
  uncheckAll: () => void;
  /** Returns true when every enabled (non-disabled) checkbox is checked. */
  areAllChecked: () => boolean;
}

export interface UserModuleMatrixProps {
  /** Currently selected accounting year (string, as used by YearSelector). */
  selectedYear: string;
  /** Called whenever the user toggles any checkbox. The parent can track dirty state. */
  onChange?: (rows: UserPrefRow[]) => void;
  /** When provided, only DOs whose targetDO is in this list are shown. Empty array = show all. */
  filterOperators?: string[];
  /** Called once the visible (Settings-enabled) operators are computed, so the parent can populate the filter. */
  onAvailableOperators?: (operators: string[]) => void;
  /** Increment to force a re-fetch of saved preferences (e.g. after import / copy / reset-all). */
  refreshKey?: number;
}

// ── Component ────────────────────────────────────────────────────────────────

const UserModuleMatrix = forwardRef<UserModuleMatrixHandle, UserModuleMatrixProps>(({
  selectedYear,
  onChange,
  filterOperators,
  onAvailableOperators,
  refreshKey = 0,
}, ref) => {
  const { t } = useTranslation();

  const yearNum = selectedYear && !isNaN(parseInt(selectedYear)) ? parseInt(selectedYear) : 0;
  const shouldFetch = yearNum > 0;

  // 1. Load Settings superset (all DOs + modules for the year)
  const {
    relations: settingsRelations,
    isLoading: isLoadingSettings,
    error: settingsError,
  } = useDesignatedOperatorRelations(yearNum, shouldFetch);

  // 2. Load user's saved preferences for the year
  const [savedPrefs, setSavedPrefs] = useState<UserPreference[]>([]);
  const [isLoadingPrefs, setIsLoadingPrefs] = useState(false);

  useEffect(() => {
    if (!shouldFetch) {
      setSavedPrefs([]);
      return;
    }

    const fetchPrefs = async () => {
      setIsLoadingPrefs(true);
      try {
        const resp = await axios.get<UserPreference[]>(`/user-preferences/${yearNum}`);
        setSavedPrefs(resp.data);
      } catch {
        // No saved preferences is a valid state — treat as empty
        setSavedPrefs([]);
      } finally {
        setIsLoadingPrefs(false);
      }
    };

    fetchPrefs();
  }, [yearNum, shouldFetch, refreshKey]);


  // 3. Derive module columns & local editable rows
  const [moduleColumns, setModuleColumns] = useState<ModuleColumn[]>([]);
  const [rows, setRows] = useState<UserPrefRow[]>([]);

  // Baseline snapshot: maps targetDO → initial modules record (set once when rows are derived)
  const baselineModulesRef = useRef<Map<string, Record<string, boolean>>>(new Map());

  useEffect(() => {
    if (!settingsRelations || settingsRelations.length === 0) {
      setModuleColumns([]);
      setRows([]);
      baselineModulesRef.current = new Map();
      return;
    }

    // Build column list from the first relation (all relations share the same modules)
    const cols: ModuleColumn[] = settingsRelations[0].modules.map((m) => ({
      id: m.name.toLowerCase(),
      name: m.name,
    }));
    setModuleColumns(cols);

    // Build a lookup: doName (operator name) → saved pref modules
    const prefsByDoName = new Map<string, Record<string, boolean>>();
    savedPrefs.forEach((pref) => {
      const map: Record<string, boolean> = {};
      pref.modules.forEach((m) => {
        map[m.name.toLowerCase()] = m.enabled;
      });
      prefsByDoName.set(pref.doName, map);
    });

    const transformedRows: UserPrefRow[] = settingsRelations.map((rel) => {
      const modules: Record<string, boolean> = {};
      const moduleEditability: Record<string, boolean> = {};
      const moduleSettingsEnabled: Record<string, boolean> = {};

      // Start from Settings as the superset
      rel.modules.forEach((m) => {
        const key = m.name.toLowerCase();
        // Default to unchecked — only checked if user has a saved preference with enabled: true
        modules[key] = false;
        moduleEditability[key] = m.editable !== false;
        // Track whether the module is enabled in Settings (controls checkbox disabled state)
        moduleSettingsEnabled[key] = m.enabled;
      });

      // Overlay with user's saved preferences (if any) — match by operator name
      const userPrefModules = prefsByDoName.get(rel.targetDO);
      if (userPrefModules) {
        Object.entries(userPrefModules).forEach(([key, enabled]) => {
          if (key in modules) {
            modules[key] = enabled;
          }
        });
      }

      return {
        settingsId: rel.id,
        originalDO: rel.originalDO,
        targetDO: rel.targetDO,
        year: rel.year,
        modules,
        moduleEditability,
        moduleSettingsEnabled,
      };
    });

    // Snapshot baseline so we can later detect which rows changed
    const baseline = new Map<string, Record<string, boolean>>();
    transformedRows.forEach((row) => {
      baseline.set(row.targetDO, { ...row.modules });
    });
    baselineModulesRef.current = baseline;

    setRows(transformedRows);
  }, [settingsRelations, savedPrefs]);

  // 4. Checkbox toggle handler
  const handleCheckboxChange = useCallback(
    (targetDO: string, moduleId: string) => {
      setRows((prev) => {
        const next = prev.map((row) =>
          row.targetDO === targetDO
            ? { ...row, modules: { ...row.modules, [moduleId]: !row.modules[moduleId] } }
            : row,
        );
        onChange?.(next);
        return next;
      });
    },
    [onChange],
  );

  // 5. Expose imperative handle to parent
  useImperativeHandle(ref, () => ({
    getRows: () => rows,
    getChangedRows: () => {
      const baseline = baselineModulesRef.current;
      return rows.filter((row) => {
        const original = baseline.get(row.targetDO);
        if (!original) return true; // new row, treat as changed
        return Object.keys(row.modules).some(
          (key) => row.modules[key] !== original[key],
        );
      });
    },
    resetRows: () => {
      setRows((prev) =>
        prev.map((row) => {
          const resetModules: Record<string, boolean> = {};
          Object.keys(row.modules).forEach((key) => {
            resetModules[key] = false;
          });
          return { ...row, modules: resetModules };
        }),
      );
      onChange?.(
        rows.map((row) => {
          const resetModules: Record<string, boolean> = {};
          Object.keys(row.modules).forEach((key) => {
            resetModules[key] = false;
          });
          return { ...row, modules: resetModules };
        }),
      );
    },
    checkAll: () => {
      const updated = rows.map((row) => {
        const newModules: Record<string, boolean> = {};
        Object.keys(row.modules).forEach((key) => {
          // Only check modules that are enabled and editable in Settings
          const canToggle = row.moduleSettingsEnabled[key] && row.moduleEditability[key];
          newModules[key] = canToggle ? true : row.modules[key];
        });
        return { ...row, modules: newModules };
      });
      setRows(updated);
      onChange?.(updated);
    },
    uncheckAll: () => {
      const updated = rows.map((row) => {
        const newModules: Record<string, boolean> = {};
        Object.keys(row.modules).forEach((key) => {
          const canToggle = row.moduleSettingsEnabled[key] && row.moduleEditability[key];
          newModules[key] = canToggle ? false : row.modules[key];
        });
        return { ...row, modules: newModules };
      });
      setRows(updated);
      onChange?.(updated);
    },
    areAllChecked: () => {
      // Returns true when every enabled (non-disabled) checkbox is checked
      return rows.every((row) =>
        Object.keys(row.modules).every((key) => {
          const canToggle = row.moduleSettingsEnabled[key] && row.moduleEditability[key];
          // If the checkbox is disabled, ignore it; otherwise it must be checked
          return !canToggle || row.modules[key];
        }),
      );
    },
  }), [rows, onChange]);


  /** A checkbox is disabled when Settings has enabled=false OR editable=false for that module. */
  const isCheckboxDisabled = (row: UserPrefRow, moduleId: string) =>
    !row.moduleSettingsEnabled[moduleId] || !row.moduleEditability[moduleId];

  /** An operator row is hidden when ALL its modules are disabled in Settings (enabled=false). */
  const isOperatorFullyDisabled = (row: UserPrefRow) =>
    Object.values(row.moduleSettingsEnabled).every((enabled) => !enabled);

  // Filter rows: hide operators where all modules are disabled in Settings
  const enabledRows = rows.filter((row) => !isOperatorFullyDisabled(row));

  /** Returns true when every enabled (non-disabled) checkbox for a module column is checked. */
  const isModuleAllChecked = (moduleId: string) =>
    enabledRows.every((row) => {
      const canToggle = row.moduleSettingsEnabled[moduleId] && row.moduleEditability[moduleId];
      return !canToggle || row.modules[moduleId];
    });

  /** Toggle all enabled checkboxes in a single module column. */
  const handleToggleModule = useCallback(
    (moduleId: string) => {
      const allChecked = enabledRows.every((row) => {
        const canToggle = row.moduleSettingsEnabled[moduleId] && row.moduleEditability[moduleId];
        return !canToggle || row.modules[moduleId];
      });
      const newValue = !allChecked;

      setRows((prev) => {
        const next = prev.map((row) => {
          const canToggle = row.moduleSettingsEnabled[moduleId] && row.moduleEditability[moduleId];
          if (!canToggle) return row;
          return { ...row, modules: { ...row.modules, [moduleId]: newValue } };
        });
        onChange?.(next);
        return next;
      });
    },
    [enabledRows, onChange],
  );

  // Notify parent of available (enabled) operators whenever they change
  useEffect(() => {
    onAvailableOperators?.(enabledRows.map((r) => r.targetDO));
  }, [enabledRows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply the optional operator filter from the parent
  const visibleRows =
    filterOperators && filterOperators.length > 0
      ? enabledRows.filter((row) => filterOperators.includes(row.targetDO))
      : enabledRows;

  // ── Render ───────────────────────────────────────────────────────────────

  const isLoading = isLoadingSettings || isLoadingPrefs;

  if (!shouldFetch) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
          border: '1px solid #E0E0E0',
          borderRadius: 1,
          backgroundColor: '#FAFAFA',
        }}
        data-testid="user-module-matrix-loading"
      >
        {t('userPreferences.matrix.loading', 'Loading module preferences...')}
      </Box>
    );
  }

  if (settingsError) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'error.main',
          border: '1px solid #E0E0E0',
          borderRadius: 1,
          backgroundColor: '#FAFAFA',
        }}
        data-testid="user-module-matrix-error"
      >
        {t('userPreferences.matrix.error', 'Failed to load module preferences')}
      </Box>
    );
  }

  if (moduleColumns.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
          border: '1px solid #E0E0E0',
          borderRadius: 1,
          backgroundColor: '#FAFAFA',
        }}
        data-testid="user-module-matrix-empty"
      >
        {t('userPreferences.matrix.empty', 'No module preferences available for this year')}
      </Box>
    );
  }

  if (visibleRows.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          color: 'text.secondary',
          border: '1px solid #E0E0E0',
          borderRadius: 1,
          backgroundColor: '#FAFAFA',
        }}
        data-testid="user-module-matrix-no-enabled"
      >
        {t('userPreferences.matrix.noEnabledOperators', 'There are no enabled operators for this year')}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }} maxWidth="770px">
      <TableContainer>
        <Table size="small" data-testid="user-module-matrix-table">
          <TableHead>
            <TableRow>
              <HeaderCell
                sx={{ backgroundColor: 'white', minWidth: '100px', borderBottom: 'none' }}
              />
              {moduleColumns.map((col, idx) => {
                const bgColor = idx % 2 === 0 ? 'white' : '#E7EFF5';
                const allChecked = isModuleAllChecked(col.id);
                return (
                  <HeaderCell
                    key={col.id}
                    align="center"
                    sx={{
                      backgroundColor: bgColor,
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                      minWidth: '80px',
                      borderTopLeftRadius: '12px',
                      borderTopRightRadius: '12px',
                      borderBottom: 'none',
                      px: 1,
                    }}
                  >
                    {col.name}
                    <SelectAllText
                      onClick={() => handleToggleModule(col.id)}
                      data-testid={`user-module-toggle-${col.id}`}
                    >
                      {allChecked
                        ? t('userPreferences.matrix.deselectAll', 'Deselect all')
                        : t('userPreferences.matrix.selectAll', 'Select all')}
                    </SelectAllText>
                  </HeaderCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell
                  colSpan={moduleColumns.length + 1}
                  columnIndex={0}
                  sx={{ textAlign: 'center', py: 3, color: 'text.secondary', borderBottom: 'none' }}
                >
                  {t(
                    'userPreferences.matrix.noRelations',
                    'No designated operator relations available',
                  )}
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              visibleRows.map((row) => {
                return (
                  <StyledTableRow
                    key={row.settingsId ?? row.targetDO}
                    data-testid={`user-pref-row-${row.targetDO}`}
                  >
                    <StyledTableCell component="th" scope="row" columnIndex={0}>
                      {row.targetDO}
                    </StyledTableCell>

                    {moduleColumns.map((col, idx) => {
                      const colType = idx % 2 === 0 ? 0 : 1;
                      const disabled = isCheckboxDisabled(row, col.id);
                      const tooltipMsg = disabled
                        ? t(
                            'userPreferences.matrix.notEditable',
                            'This module is not editable for this operator',
                          )
                        : '';

                      return (
                        <StyledTableCell
                          key={`${row.targetDO}-${col.id}`}
                          align="center"
                          padding="checkbox"
                          columnIndex={colType}
                          sx={{ minWidth: '80px', px: 1 }}
                        >
                          <Tooltip title={tooltipMsg}>
                            <span>
                              <StyledCheckbox
                                checked={row.modules[col.id] ?? false}
                                onChange={() => handleCheckboxChange(row.targetDO, col.id)}
                                size="small"
                                disabled={disabled}
                                data-testid={`user-pref-checkbox-${row.targetDO}-${col.id}`}
                              />
                            </span>
                          </Tooltip>
                        </StyledTableCell>
                      );
                    })}
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  );
});

UserModuleMatrix.displayName = 'UserModuleMatrix';

export default UserModuleMatrix;

